import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';
import { UpdateReleaseStoreError } from '@webapp/store/release/action-creators';

import { TrackType } from '@common/types/release/track-type';
import { ReleaseType } from '@common/types/release/release-type';

import ErrorMessage from '@webapp/components/basics/error-message/index';

import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';

interface ExplicitContentCtrlProps {
  id?: string;
  className?: string;
  trackId: string;
  validate?: boolean;
  releaseId?: string;
}

interface StoreProps {
  isExplicit?: boolean;
  isrcExists?: boolean;
  isClean: boolean;
  hasCleanVersion?: boolean;
  releaseType: ReleaseType;
  trackCount: number;
  trackType: TrackType;
  isUploading: boolean;
  disableCleanUpload?: boolean;
  updateExplicit: (value: boolean) => UpdateTrackField<'isExplicit'>;
  updateClean: (value?: boolean) => UpdateTrackField<'isClean'>;
  updateType: (value: TrackType) => UpdateTrackField<'type'>;
  updateHasCleanVersion: (
    value: boolean
  ) => UpdateTrackField<'hasCleanVersion'>;
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => UpdateReleaseStoreError;
}

const mapStateToProps = (state: AppState, props: ExplicitContentCtrlProps) => {
  const list = trackList(state.tracks.tracks, state.release.release?.id);

  const isUploading: boolean = list
    .filter((t) => t.isClean)
    .reduce((memo: boolean, t) => {
      if (t.id) {
        const asset = state.assets.assets[t.id];
        if (asset && asset.status === 'uploading') {
          return true;
        }
      }
      return memo;
    }, false);

  return {
    isExplicit: state.tracks.tracks[props.trackId].isExplicit,
    isrcExists: state.tracks.tracks[props.trackId].isrcExists,
    isClean: !!state.tracks.tracks[props.trackId].isClean,
    trackType: state.tracks.tracks[props.trackId].type || 'song',
    hasCleanVersion: state.tracks.tracks[props.trackId].hasCleanVersion,
    trackCount: list.length,
    releaseType: state.release.release?.type || 'single',
    isUploading,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  props: ExplicitContentCtrlProps
) => ({
  updateExplicit: (value: boolean) =>
    dispatch(
      new UpdateTrackField<'isExplicit'>(props.trackId, 'isExplicit', value)
    ),
  updateClean: (value?: boolean) =>
    dispatch(new UpdateTrackField<'isClean'>(props.trackId, 'isClean', value)),
  updateHasCleanVersion: (value?: boolean) =>
    dispatch(
      new UpdateTrackField<'hasCleanVersion'>(
        props.trackId,
        'hasCleanVersion',
        value
      )
    ),
  updateType: (value: TrackType) =>
    dispatch(new UpdateTrackField<'type'>(props.trackId, 'type', value)),
  sendAnalytics: (
    field: string,
    value: string,
    error: string,
    releaseId?: string
  ) => dispatch(new UpdateReleaseStoreError(error, field, value, releaseId)),
});

const ExplicitDropdown = GenericDropdownBuilder<boolean>();

export const ExplicitContentCtrl: React.FC<
  ExplicitContentCtrlProps & StoreProps
> = ({ validate, releaseId, ...props }) => {
  const { isExplicit, sendAnalytics, isrcExists } = props;
  const showCtrl = props.trackType === 'song' && !props.isClean;

  const handleChange = (value: boolean) => {
    props.updateExplicit(value);

    if (value) {
      props.updateClean(undefined);
      props.updateType('song');
    }
  };

  React.useEffect(() => {
    if (validate && isExplicit === undefined) {
      sendAnalytics(
        'Release Explicit content',
        '',
        'RELEASE.MISSING_EXPLICIT_CONTENT',
        releaseId
      );
    }
  }, [validate, releaseId, isExplicit, sendAnalytics]);

  const options = [
    {
      value: true,
      label: 'Yes, Explicit',
      helpText: 'Yes this track contains explicit language or themes.',
    },
    {
      value: false,
      label: 'No, not Explicit (Clean)',
      helpText: 'No, this track contains no explicit language or themes.',
    },
  ];

  return showCtrl ? (
    <div>
      <ExplicitDropdown
        id={props.id}
        className={props.className}
        title={<Translate id="RELEASE.TRACK_INFO.TRACK.EXPLICIT" />}
        value={props.isExplicit}
        onChange={(value) => handleChange(value)}
        options={options}
        disabled={isrcExists}
      />
      {validate && props.isExplicit === undefined && (
        <ErrorMessage
          hasBackground
          className="mt-5"
          errorId="RELEASE.MISSING_EXPLICIT_CONTENT"
        />
      )}
    </div>
  ) : null;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExplicitContentCtrl);
